import React from 'react';
import { Box, Typography } from '@mui/material';
import { SizeMe } from 'react-sizeme';
import { conditionalAssignment } from '../../../../lib/Object';
import { getContentDescription, getContentName } from '../Content/helper';
import { RoomContentContextProps } from '../../../../lib/RoomContentContext';
import { RoomStructureContent } from '../../../../definitions/schemas';
import { SimplifiedTrackingProps } from '../../../Rooms/Tracking/types';
import { UrlViewerVariant } from '../../../ContentUrl/UrlViewer';
import RoomContentFacade from '../../../Rooms/RoomContentFacade';
import ShowMoreLess from '../../../Content/ShowMoreLess';
import TextDecorator from '../../../TextDecorator';
import useDarkTheme from '../../../../theme/styles/useDarkTheme';
import { hasCover } from '../../../Content/helper';
import { StorageProviderMTPickers } from '../../../../enums/Integrations/StorageProviders';

export interface TheaterModeProps {
  content: RoomStructureContent;
  contentDownloadTrigger: number;
  contextProps: RoomContentContextProps;
  disableDocumentsDownloadButton?: boolean;
  playMediaImmediately: boolean;
  referrer: string;
  TrackingProps?: SimplifiedTrackingProps;
}

const TheaterMode: React.FC<TheaterModeProps> = (
  props: TheaterModeProps
): JSX.Element => {
  const darkThemeClasses = useDarkTheme();

  const {
    content: structureContent,
    contentDownloadTrigger,
    contextProps,
    disableDocumentsDownloadButton = false,
    playMediaImmediately,
    referrer,
    TrackingProps,
  } = props;

  const [desc, descVisible] = getContentDescription(structureContent, true);
  const [name, nameVisible] = getContentName(structureContent, true);

  const { isDownloadable, match: content, url } = structureContent;
  const hasContentCover = hasCover(content, structureContent);

  const isIframeEmbeddedProvider =
    content.mtData?.provider === StorageProviderMTPickers.AssetHub ||
    content.mtData?.provider === StorageProviderMTPickers.CallAi;

  return (
    <>
      <Box mx={isIframeEmbeddedProvider ? 4 : 2}>
        {nameVisible && (
          <Typography
            className={darkThemeClasses.primaryTextLight}
            variant="h5"
          >
            {name}
          </Typography>
        )}
        {descVisible && (
          <Box mt={name ? 1 : 0}>
            <ShowMoreLess className={darkThemeClasses.linkLight} text={desc}>
              <TextDecorator
                className={darkThemeClasses.linkLight}
                component="div"
                disableAnchorIcon
                disableTrim
                text={desc}
                TypographyProps={{
                  className: darkThemeClasses.secondaryTextLight,
                }}
                whiteSpace="pre-wrap"
              />
            </ShowMoreLess>
          </Box>
        )}
      </Box>
      <Box mt={nameVisible || descVisible ? 4 : 2}>
        <SizeMe refreshMode="debounce">
          {(sizeMeProps) => (
            <RoomContentFacade
              autoPlay
              content={content}
              contentDescription={desc}
              contentId={structureContent.id}
              contentName={name}
              contentUrl={url}
              contextProps={contextProps}
              darkTheme
              disableImageContentName={!nameVisible}
              downloadTrigger={contentDownloadTrigger}
              hasBorder
              isDownloadable={isDownloadable}
              referrer={referrer}
              RoomAudioContentProps={{
                /**
                 * Overwriting the autoPlay prop, because audios that don't have a cover
                 * shouldn't autoplay if the content is reached from the theater
                 * mode navigation
                 */
                autoPlay: playMediaImmediately || hasContentCover,
                disableDefaultCover: playMediaImmediately || !hasContentCover,
                disableMinHeight: !hasContentCover,
              }}
              RoomDocumentContentProps={{
                disableDownloadButton: disableDocumentsDownloadButton,
                theaterMode: true,
              }}
              RoomEmbeddableContentProps={{
                hasBorder: true,
              }}
              RoomImageContentProps={{
                rounded: false,
              }}
              RoomTextContentProps={{
                largeFontSize: true,
                /**
                 * Text Boxes could have their in-memory text overwritten in
                 * the structure if the room hasn't been saved yet and a new
                 * version of their content is created. So if there's any text
                 * on the structure, it should be used instead of the one
                 * from the content record.
                 *
                 * @note this only happens for edit room and preview room flows
                 */
                ...conditionalAssignment(!!structureContent.text, {
                  text: structureContent.text,
                }),
              }}
              RoomURLContentProps={{
                forceFullScreen: true,
                variant: UrlViewerVariant.Block,
              }}
              RoomVideoContentProps={{
                disableDefaultCover: playMediaImmediately,
              }}
              rounded
              structureContent={structureContent}
              version={content.version}
              width={sizeMeProps.size.width}
              {...TrackingProps}
            />
          )}
        </SizeMe>
      </Box>
    </>
  );
};

export default TheaterMode;
